import { useFetchSubtypesQuery } from "@store/master";
import { DatePicker, Input, Select } from "antd";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

const inputClassName = "!rounded-lg border-[#D0D5DD] !text-xs !h-8";

const getDatePickerClassName = (hasValue) =>
  `!w-full !rounded-lg !h-8 [&_.ant-picker-input>input]:!leading-none ${
    hasValue
      ? "[&_.ant-picker-input>input]:!text-xs"
      : "[&_.ant-picker-input>input]:!text-xs"
  }`;

export default function FilterArea({ onFiltersChange }) {
  const [selectedType, setSelectedType] = useState(undefined);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const { data: subtypesData, isLoading: isSubtypesLoading } = useFetchSubtypesQuery({});

  const getFilterOutput = useCallback(
    (type, search, from, to) => {
      const output = {
        subTypeMasterId: type ?? null,
        search: search?.trim() ? search.trim() : null,
      };

      if (from && to) {
        output.fromDate = from;
        output.toDate = to;
      }

      return output;
    },
    []
  );

  const applyFilters = useCallback(
    (type, search, from, to) => {
      const output = getFilterOutput(type, search, from, to);
      onFiltersChange?.(output);
    },
    [getFilterOutput, onFiltersChange]
  );

  const debouncedApplyFilters = useMemo(
    () =>
      debounce((type, search, from, to) => {
        applyFilters(type, search, from, to);
      }, 300),
    [applyFilters]
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setDebouncedSearchText(value);
      }, 300),
    []
  );

  const notifyFilterChange = useCallback(() => {
    debouncedApplyFilters(
      selectedType,
      debouncedSearchText,
      fromDate,
      toDate
    );
  }, [
    debouncedApplyFilters,
    selectedType,
    debouncedSearchText,
    fromDate,
    toDate,
  ]);

  useEffect(() => {
    notifyFilterChange();
  }, [notifyFilterChange]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      debouncedApplyFilters.cancel();
    };
  }, [debouncedSearch, debouncedApplyFilters]);

  const handleTypeChange = (value) => {
    setSelectedType(value);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleFromDateChange = (date) => {
    setFromDate(date ? date.format("YYYY-MM-DD") : null);
  };

  const handleToDateChange = (date) => {
    setToDate(date ? date.format("YYYY-MM-DD") : null);
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-2.5 sm:gap-3 md:w-[30%] lg:w-[22%] xl:w-[18%] md:shrink-0 md:pr-2.5 md:border-r md:border-gray-200 animate-fadeIn custom-scrollbar">
      <div className="grid grid-cols-1 gap-2.5 min-w-0 sm:grid-cols-2 md:grid-cols-1">
        <div className="min-w-0 space-y-1.5 sm:space-y-2">
        <label
          htmlFor="manage-filter-type"
          className="text-xs font-semibold text-gray-700 sm:text-sm"
        >
          Type
        </label>
        <Select
          id="manage-filter-type"
          allowClear
          placeholder="Select type"
          options={subtypesData?.data?.flatMap((item) =>
            item.SubTypeMasters.filter((subtype) => {
              const name = String(subtype.name).toLowerCase()
              return name.includes("import") || name.includes("export")
            }).map((subtype) => ({
              label: subtype.name,
              value: subtype.id,
            }))
          )}
          value={selectedType}
          loading={isSubtypesLoading}
          onChange={handleTypeChange}
          className="!w-full !h-8 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!border-[#D0D5DD] [&_.ant-select-selector]:!text-xs [&_.ant-select-selector]:!h-8 [&_.ant-select-selector]:!items-center"
        />
        </div>

        <div className="min-w-0 space-y-1.5 sm:space-y-2">
        <label
          htmlFor="manage-filter-search"
          className="text-xs font-semibold text-gray-700 sm:text-sm"
        >
          Search
        </label>
        <Input
          id="manage-filter-search"
          placeholder="Search"
          value={searchText}
          onChange={handleSearchChange}
          className={inputClassName}
          allowClear
        />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 min-w-0 min-[420px]:grid-cols-2 md:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor="manage-filter-from-date"
            className="text-xs font-semibold text-gray-700 sm:text-sm"
          >
            From
          </label>
          <DatePicker
            id="manage-filter-from-date"
            format="DD-MM-YYYY"
            placeholder="From date"
            value={fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null}
            onChange={handleFromDateChange}
            className={getDatePickerClassName(!!fromDate)}
            aria-label="From date"
            popupClassName="small-datepicker-popup"
          />
        </div>

        <div className="flex min-w-0 flex-col gap-1.5 sm:gap-2">
          <label
            htmlFor="manage-filter-to-date"
            className="text-xs font-semibold text-gray-700 sm:text-sm"
          >
            To
          </label>
          <DatePicker
            id="manage-filter-to-date"
            format="DD-MM-YYYY"
            placeholder="To date"
            value={toDate ? dayjs(toDate, "YYYY-MM-DD") : null}
            onChange={handleToDateChange}
            className={getDatePickerClassName(!!toDate)}
            aria-label="To date"
            popupClassName="small-datepicker-popup"
          />
        </div>
      </div>
    </div>
  );
}
